import { IMsalContext, useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionType,
  IPublicClientApplication
} from '@azure/msal-browser';
import { Providers, ProviderState } from '@microsoft/mgt-react';
import { CacheService, SimpleProvider } from '@microsoft/mgt-element';
import { Client, GraphClientError } from '@microsoft/microsoft-graph-client';
import { User } from 'microsoft-graph';

// @ts-ignore
Providers.globalProvider = undefined;

const defaultPhoto =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIYAAACGCAMAAAAvpwKjAAAAMFBMVEXh4eGjo6Pk5OSgoKDd3d2tra3T09PGxsanp' +
  '6fDw8PY2NjLy8u1tbWdnZ2xsbHAwMBDsMj6AAAC/klEQVR4nO2b23asIAxAJVxELvL/f3vQjtP2jJcoBO1a2U/z1t0QFJLYdQzDMAzDMMxfAjJ3' +
  'Gyhn+oxx6kYXH6yQM8IGf4sCqCSiFG9kFEm1j0iSPxxeJjK1dQCnPyRmEe0aBgSMWLXIHsI08wAf1yUmom/kkWOxS6N4OLuvYV0LC1jPzp952i' +
  'Ac0B9YZI+e3kMdLMm8LOQWMBwGI4djIA+HPrYQQlNbGEQwcjgMrQUknEaiXZXD3frSoN6ziH0yQbxXFCoYORyKVMNhNUgf6IDbKNNWoUyOh2g8' +
  'ZFGekqKA3bDEj6+Ae3wFYg2P06A+kB6dAF9rQn4OxKyKDNQWYHYuBwuxweF8PD6LjuQSmCeYbHGDPDz5UJ95FvazlD4/F/bSo0lifKG2j4JS075' +
  'NfrO1Lu1WZAb8WoVDigbXxt8eLvwvIkVoWet5iyQrlwJY/mHTDRKzSGdS0DajQzLdfYXRuSar1PKLYRiG+avc2/Kb/rhyxvgZY5zqWvvkP+fMEE' +
  'ZtxdLyE1aPYTCu2TsuK/RBi7jWa4tCh96Rv/IBlA/fh53V02g+AAVP2JZdHLYVvlXITABMdjhWeKvYYKqLQOd1PCExi0Ttq6bJJIFZjA8RWVMEzC' +
  'WJl0ilWgeogKisbBNDjdY9mDOJuRoRWx6QfEusQGlhEFLRgizEssoLoumKo6g1i24YIDwK8gPT+sViL5dfkG1GHAWFuYoW2eNqMPamRc5zeb4E17' +
  'TAcrks9hANTJUeT7zat3/KTlHH3QK0xVhQtnW1LIQo6TehmjgYChs9ld5txUNHMFSIRyyf9YG+2CPWqKSjZpv2qDT3VBiPKrGYPTbnMhGhqDipuD' +
  'WlirCoO8d68a6S7ygVJbpr15UaF5QPD5XOZYgkmrTOV9kTKxNrXV7XREZcdUHGkXJaADrz0elbW45A3fICcGm34CKjTq5FOTCbDKNcrwTKcWji8M' +
  'YMQS8fQnx9DKHDQDwmusL0PytnvJ8+DPF+qhLf2X4EuP87GYZhGIZhGOYc/wA+txx3nmYdOgAAAABJRU5ErkJggg==';
/**
 * Converts a Blob to a readable URL via `URL.createObjectURL`, used for rendering user profile photos.
 */
const blobToImageUrl = (blob: Blob): string => window.URL.createObjectURL(blob);

/**
 * Fetches the currently authenticated user's profile and photo from the Microsoft Graph API in parallel.
 */
const getCurrentUser = async (): Promise<any> => {
  const [user, photoBlob] = await Promise.all([
    Providers.globalProvider.graph.client.api('/me').get(),
    Providers.globalProvider.graph.client.api('/me/photo/$value').get()
  ]);
  let photo = defaultPhoto;
  if (photoBlob) photo = blobToImageUrl(photoBlob);
  return { user, photo };
};

/**
 * Fetches a user by email address from the Microsoft Graph API.
 *
 * By default includes the user's photo. Pass `includeImage: false` to skip the photo request and avoid an extra API call.
 *
 * @param email - The user's email address
 * @param includeImage - Whether to fetch and include the user's profile photo. Defaults to `true`.
 * @returns A promise resolving to `{ user: User, photo?: any }` — `photo` is omitted when `includeImage` is `false`.
 */
const getUser = async (email: string, includeImage = true): Promise<{ user: User; photo?: any }> => {
  if (includeImage) {
    const [user, photoBlob] = await Promise.all([
      Providers.globalProvider.graph.client.api(`/users/${email}`).get() as Promise<User>,
      Providers.globalProvider.graph.client.api(`/users/${email}/photo/$value`).get()
    ]);
    let photo = defaultPhoto;
    if (photoBlob) photo = blobToImageUrl(photoBlob);

    return { user, photo };
  } else {
    const user = (await Providers.globalProvider.graph.client.api(`/users/${email}`).get()) as User;
    return { user };
  }
};

/**
 * Retrieves an access token for Microsoft Graph API using MSAL's token acquisition flow.
 *
 * Throws `GraphClientError` if scopes are empty. On silent token failure, attempts interactive login via redirect or popup — if both fail it re-throws the original error.
 *
 * @param options - Object containing MSAL options and the public client application instance
 * @param publicClientApplication - Object with `scopes` (array), `account` (MSAL account), and `interactionType` (`Redirect` or `Popup`); also `publicClientApplication` (the MSAL instance)
 * @returns A promise resolving to the access token string.
 */
async function getAccessToken({
  options,
  publicClientApplication
}: {
  options: any;
  publicClientApplication: IPublicClientApplication;
}): Promise<string | any> {
  const scopes = options && options.scopes;
  const account = options && options.account;
  const error = new GraphClientError();
  if (!scopes || scopes.length === 0) {
    error.name = 'Empty Scopes';
    error.message = 'Scopes cannot be empty, Please provide scopes';
    throw error;
  }
  try {
    const response: AuthenticationResult = await publicClientApplication.acquireTokenSilent({
      scopes,
      account
    });
    if (!response || !response.accessToken) {
      error.name = 'Access token is undefined';
      error.message = 'Received empty access token from PublicClientApplication';
      throw error;
    }
    return new Promise((resolve) => resolve(response.accessToken));
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      if (options.interactionType === InteractionType.Redirect) {
        await publicClientApplication.acquireTokenRedirect({ scopes });
      } else if (options.interactionType === InteractionType.Popup) {
        const response: AuthenticationResult = await publicClientApplication.acquireTokenPopup({ scopes });
        return response.accessToken;
      }
    } else {
      throw error;
    }
  }
}
/**
 * Default Microsoft Graph API scopes used for authentication and data access.
 *
 * Includes openid/profile/email for basic login, offline_access for refresh tokens, user.read (read-only and read-write), people.read, presence.read.all, and read/write access to SharePoint Sites and Files.
 */
const graphScopes = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'user.readbasic.all',
  'user.readwrite ',
  'people.read',
  'presence.read.all',
  'Sites.Read.All',
  'Files.ReadWrite.All'
];
/**
 * Initializes the Microsoft Graph global provider with an MSAL context for authenticated API calls.
 *
 * Configures `SimpleProvider` with an auth provider that acquires tokens via MSAL's `acquireTokenSilent`. Sets ~30-day cache for users, people, and photos; 2-hour general response cache; enables presence with 5-minute cache.
 *
 * @param msalContext - An IMsalContext from `useMsal()` containing the MSAL instance and active account
 * @returns The configured `SimpleProvider` instance (now set as `Providers.globalProvider`).
 */
export function initGraphService(msalContext: IMsalContext) {
  const authProvider: any = {
    publicClientApplication: msalContext.instance,
    options: {
      account: msalContext.instance.getActiveAccount()!,
      scopes: graphScopes,
      interactionType: InteractionType.Redirect
    }
  };
  Providers.globalProvider = new SimpleProvider(() => getAccessToken(authProvider));
  Providers.globalProvider.setState(ProviderState.SignedIn);
  CacheService.config.users.invalidationPeriod = 2592000000; // ~30 days of caching
  CacheService.config.people.invalidationPeriod = 2592000000;
  CacheService.config.photos.invalidationPeriod = 2592000000;
  CacheService.config.response.invalidationPeriod = 1000 * 60 * 60 * 2; // 2hr cache
  CacheService.config.presence.isEnabled = true;
  CacheService.config.presence.invalidationPeriod = 1000 * 60 * 5; // 5 min cache
  return Providers.globalProvider;
}

/**
 * Graph Service hook
 */
export function useGraphService(): {
  getCurrentUser: () => Promise<any>;
  getUser: (email: string, includeImage?: boolean) => Promise<{ user: User; photo?: any }>;
  graphClient: typeof Providers.client
} {
  const msal = useMsal();
  const isAuthenticated = useIsAuthenticated();
  if (!Providers.globalProvider && isAuthenticated) setTimeout(() => initGraphService(msal), 250);
  return { getCurrentUser, getUser, graphClient: Providers.client };
}
